import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { LifeBuoy, Loader2, X, Navigation, Radio, CheckCircle2, MapPin } from "lucide-react";
import { toast } from "sonner";

const userIcon = L.divIcon({ className: "yh-user-marker", iconSize: [22, 22], iconAnchor: [11, 11],
  html: '<div style="width:16px;height:16px;border-radius:999px;background:#2563eb;border:3px solid white;box-shadow:0 0 0 3px rgba(37,99,235,0.35);"></div>' });
const rescuerIcon = L.divIcon({ className: "yh-vehicle-marker", iconSize: [30, 30], iconAnchor: [15, 15],
  html: '<div style="width:26px;height:26px;border-radius:8px;background:#FFC107;border:2px solid #111;display:grid;place-items:center;font-weight:800;color:#111;">🛻</div>' });

const STATUS_LABEL = {
  pending: "Şoför yanıtı bekleniyor",
  accepted: "Kurtarıcı talebi kabul etti",
  driver_arriving: "Kurtarıcı size geliyor",
  arrived: "Kurtarıcı yerinde",
  completed: "Görev tamamlandı",
};

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!map || points.length < 1) return;
    if (points.length === 1) {
      map.setView(points[0], 14);
    } else {
      map.fitBounds(L.latLngBounds(points), { padding: [40, 40] });
    }
  }, [map, JSON.stringify(points)]); // eslint-disable-line
  return null;
}
import { useMap } from "react-leaflet";

export default function CustomerTracking({ request, onEnded, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const status = request?.status;

  const customerPoint = useMemo(() => (request ? [request.customer_lat, request.customer_lng] : null), [request]);
  const vehiclePoint = useMemo(() => {
    const loc = request?.vehicle?.last_location;
    if (loc?.latitude && loc?.longitude && status !== "pending") return [loc.latitude, loc.longitude];
    return null;
  }, [request, status]);

  const points = [customerPoint, vehiclePoint].filter(Boolean);
  const distanceKm = useMemo(() => {
    if (!customerPoint || !vehiclePoint) return null;
    const [a, b] = customerPoint, [c, d] = vehiclePoint;
    const R = 6371;
    const dLat = (c - a) * Math.PI / 180;
    const dLon = (d - b) * Math.PI / 180;
    const A = Math.sin(dLat/2)**2 + Math.cos(a*Math.PI/180)*Math.cos(c*Math.PI/180)*Math.sin(dLon/2)**2;
    return +(2 * R * Math.asin(Math.sqrt(A))).toFixed(2);
  }, [customerPoint, vehiclePoint]);

  const etaMin = distanceKm != null ? Math.max(1, Math.round((distanceKm / 40) * 60)) : null;

  const cancel = async () => {
    if (busy) return;
    if (!window.confirm("Yardım talebini iptal etmek istediğinize emin misiniz?")) return;
    setBusy(true);
    try {
      await api.post(`/help-requests/${request.id}/cancel`);
      toast.success("Talep iptal edildi");
      onEnded?.();
    } catch { toast.error("İptal başarısız"); }
    finally { setBusy(false); }
  };

  if (!request) return null;

  return (
    <section className="mx-auto max-w-4xl px-4 py-4" data-testid="customer-tracking">
      <div className="rounded-2xl border-2 border-primary bg-primary/5 p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            {status === "pending" && <Loader2 className="w-5 h-5 animate-spin text-primary" />}
            {status === "accepted" && <CheckCircle2 className="w-5 h-5 text-[hsl(var(--success))]" />}
            {(status === "driver_arriving" || status === "arrived") && <Radio className="w-5 h-5 text-[hsl(var(--success))] animate-pulse" />}
            <div>
              <div className="text-xs uppercase font-semibold tracking-wider text-muted-foreground">Yardım Talebi</div>
              <div className="font-bold text-lg" data-testid="ct-status">{STATUS_LABEL[status] || status}</div>
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={onRefresh} className="rounded-full"><Radio className="w-4 h-4" /></Button>
        </div>

        <div className="text-sm">
          <div><span className="text-muted-foreground">Firma:</span> <b>{request.business?.name || "—"}</b></div>
          {request.vehicle && <div className="text-xs text-muted-foreground">{request.vehicle.name} · {request.vehicle.vehicle_type}</div>}
          {distanceKm != null && (
            <div className="mt-1 text-sm">
              <MapPin className="w-3.5 h-3.5 inline mr-1" />
              <b data-testid="ct-distance">{distanceKm < 1 ? Math.round(distanceKm*1000)+" m" : distanceKm+" km"}</b> uzakta
              {etaMin && <span className="text-muted-foreground"> · Tahmini ≈ {etaMin} dk</span>}
            </div>
          )}
        </div>

        {points.length >= 1 && (
          <div className="rounded-xl overflow-hidden border border-border h-64 mt-3">
            <MapContainer center={points[0]} zoom={13} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
              <FitBounds points={points} />
              {customerPoint && <Marker position={customerPoint} icon={userIcon}><Popup>Sizin konumunuz</Popup></Marker>}
              {vehiclePoint && <Marker position={vehiclePoint} icon={rescuerIcon}><Popup>{request.business?.name}</Popup></Marker>}
            </MapContainer>
          </div>
        )}

        <div className="flex gap-2 mt-3">
          {vehiclePoint && (
            <Button asChild size="sm" variant="outline" className="rounded-full" data-testid="ct-directions">
              <a href={`https://www.google.com/maps/dir/?api=1&destination=${vehiclePoint[0]},${vehiclePoint[1]}`} target="_blank" rel="noopener noreferrer">
                <Navigation className="w-4 h-4 mr-1" /> Yol Tarifi
              </a>
            </Button>
          )}
          {status !== "arrived" && (
            <Button size="sm" variant="destructive" onClick={cancel} disabled={busy} data-testid="ct-cancel" className="rounded-full">
              <X className="w-4 h-4 mr-1" /> İptal Et
            </Button>
          )}
        </div>

        {status === "pending" && (
          <p className="text-xs text-muted-foreground mt-2">Şoför yanıtı bekleniyor... Ekran otomatik güncellenir.</p>
        )}
      </div>
    </section>
  );
}
